import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, type Catalog, type CostBucket } from "../lib/api";
import { fmtNum } from "../lib/format";

const fmtUsd = (v: number | null | undefined) => (v == null ? "—" : v < 0.01 ? `$${v.toFixed(5)}` : `$${v.toFixed(2)}`);
const fmtTok = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

export function Costs() {
  const [days, setDays] = useState(30);
  const costs = useQuery({ queryKey: ["costs", days], queryFn: () => api.costs(days), refetchInterval: 30_000 });
  const catalog = useQuery({ queryKey: ["models"], queryFn: api.modelsCatalog });
  const d = costs.data;

  return (
    <div className="page">
      <div className="page-head">
        <h2>Model costs</h2>
        <span className="muted">usage from every captured model query · prices from the model catalog, applied retroactively</span>
        <span style={{ flex: 1 }} />
        <div className="row" role="group" aria-label="time range">
          {[7, 30, 90].map((n) => (
            <button key={n} className={`small chip${days === n ? " primary" : ""}`} onClick={() => setDays(n)}>
              {n}d
            </button>
          ))}
        </div>
      </div>

      {d && (
        <>
          <div className="tiles" style={{ marginBottom: 12 }}>
            <StatTile label={`est. cost · ${d.days}d`} value={fmtUsd(d.totals.cost_usd)} sub={d.unpriced.length ? `+ ${d.unpriced.length} unpriced model${d.unpriced.length > 1 ? "s" : ""}` : "all models priced"} />
            <StatTile label="model calls" value={fmtNum(d.totals.calls)} />
            <StatTile label="tokens in" value={fmtTok(d.totals.tokens_in)} sub={d.totals.cache_read ? `+ ${fmtTok(d.totals.cache_read)} cache read` : undefined} />
            <StatTile label="tokens out" value={fmtTok(d.totals.tokens_out)} />
          </div>

          <div className="panel">
            <h3>Tokens per day</h3>
            <DayChart perDay={d.perDay} />
            <details>
              <summary className="muted" style={{ cursor: "pointer", fontSize: 12 }}>as a table</summary>
              <BucketTable rows={d.perDay} keyLabel="day" />
            </details>
          </div>

          <div className="split">
            <div className="panel">
              <h3>By model</h3>
              <BucketTable rows={d.perModel} keyLabel="model" />
              <PriceEditor catalog={catalog.data} highlight={d.unpriced} />
            </div>
            <div className="panel">
              <h3>By purpose</h3>
              <BucketTable rows={d.perPurpose} keyLabel="purpose" />
              <h3 style={{ marginTop: 16 }}>By device</h3>
              <BucketTable rows={d.perDevice} keyLabel="device" nameOf={(b: any) => b.nickname ?? b.key.slice(0, 8)} />
              {(d.perVariant?.length ?? 0) > 0 && (
                <>
                  <h3 style={{ marginTop: 16 }}>By variant</h3>
                  <BucketTable rows={d.perVariant} keyLabel="experiment / option" />
                </>
              )}
            </div>
          </div>
        </>
      )}
      {costs.isLoading && <div className="muted">loading…</div>}
    </div>
  );
}

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      <div style={{ fontSize: 24, fontWeight: 650 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

/** Stacked per-day bars: tokens in (series-1) + tokens out (series-2). One axis, plain SVG. */
function DayChart({ perDay }: { perDay: CostBucket[] }) {
  const W = 720;
  const H = 180;
  const pad = { l: 44, r: 8, t: 8, b: 20 };
  const max = Math.max(1, ...perDay.map((b) => b.tokens_in + b.tokens_out));
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const bw = Math.min(28, Math.max(4, iw / Math.max(1, perDay.length) - 2));
  const x = (i: number) => pad.l + (iw / Math.max(1, perDay.length)) * i + 1;
  const y = (v: number) => pad.t + ih * (1 - v / max);
  const ticks = [0, 0.5, 1].map((f) => Math.round(max * f));
  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={W} height={H} role="img" aria-label="tokens per day, stacked in and out">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} className="grid" />
            <text x={pad.l - 6} y={y(t) + 3} textAnchor="end" className="tick">
              {fmtTok(t)}
            </text>
          </g>
        ))}
        {perDay.map((b, i) => {
          const total = b.tokens_in + b.tokens_out;
          const yOut = y(b.tokens_out);
          const yTop = y(total);
          return (
            <g key={b.key}>
              <title>{`${b.key}\nin ${fmtTok(b.tokens_in)} · out ${fmtTok(b.tokens_out)} · ${b.calls} calls · ${fmtUsd(b.cost_usd)}`}</title>
              {/* out at the baseline, in stacked above, 2px surface gap between segments */}
              <rect x={x(i)} width={bw} y={yOut} height={pad.t + ih - yOut} rx={2} className="s2" />
              <rect x={x(i)} width={bw} y={yTop} height={Math.max(0, yOut - yTop - 2)} rx={2} className="s1" />
            </g>
          );
        })}
        {perDay.map((b, i) =>
          i % Math.ceil(perDay.length / 8) === 0 ? (
            <text key={b.key} x={x(i) + bw / 2} y={H - 6} textAnchor="middle" className="tick">
              {b.key.slice(5)}
            </text>
          ) : null,
        )}
      </svg>
      <div className="legend">
        <span>
          <i className="swatch s1" /> tokens in
        </span>
        <span>
          <i className="swatch s2" /> tokens out
        </span>
      </div>
    </div>
  );
}

function BucketTable({ rows, keyLabel, nameOf }: { rows: CostBucket[]; keyLabel: string; nameOf?: (b: CostBucket) => string }) {
  if (!rows.length) return <div className="muted">nothing in this window</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="table">
        <thead>
          <tr>
            <th>{keyLabel}</th>
            <th>calls</th>
            <th>in</th>
            <th>out</th>
            <th>cache</th>
            <th>est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.key}>
              <td>{nameOf ? nameOf(b) : b.key}</td>
              <td>{fmtNum(b.calls)}</td>
              <td>{fmtTok(b.tokens_in)}</td>
              <td>{fmtTok(b.tokens_out)}</td>
              <td>{b.cache_read || b.cache_write ? fmtTok(b.cache_read + b.cache_write) : "—"}</td>
              <td>{fmtUsd(b.cost_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Edit per-model $/Mtok in place; writes go to the catalog SSOT (PUT /api/models). */
function PriceEditor({ catalog, highlight }: { catalog: Catalog | undefined; highlight: string[] }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: api.modelsPut,
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["models"] });
      qc.invalidateQueries({ queryKey: ["costs"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  if (!catalog) return null;
  const setPrice = (providerId: string, modelId: string, field: "input" | "output", v: string) => {
    const n = Number(v);
    const providers = catalog.providers.map((p) =>
      p.id !== providerId
        ? p
        : {
            ...p,
            models: p.models.map((m) => {
              if (m.id !== modelId) return m;
              const price = { input: 0, output: 0, ...m.price, [field]: Number.isFinite(n) && n >= 0 ? n : 0 };
              return { ...m, price };
            }),
          },
    );
    save.mutate(providers);
  };
  return (
    <details style={{ marginTop: 10 }} open={highlight.length > 0}>
      <summary className="muted" style={{ cursor: "pointer", fontSize: 12 }}>
        prices ($ per Mtok — saved to the model catalog{highlight.length ? ` · ${highlight.length} unpriced` : ""})
      </summary>
      {error && <div className="error-bar">{error}</div>}
      <table className="table">
        <thead>
          <tr>
            <th>model</th>
            <th>in $/M</th>
            <th>out $/M</th>
          </tr>
        </thead>
        <tbody>
          {catalog.providers.flatMap((p) =>
            p.models.map((m) => (
              <tr key={`${p.id}/${m.id}`} style={highlight.includes(m.id) ? { outline: "1px solid var(--accent)" } : undefined}>
                <td>
                  <span className="muted">{p.label} / </span>
                  {m.id}
                </td>
                <td>
                  <input className="price-in" type="number" min={0} step="0.01" defaultValue={m.price?.input ?? ""} onBlur={(e) => e.target.value !== String(m.price?.input ?? "") && setPrice(p.id, m.id, "input", e.target.value)} />
                </td>
                <td>
                  <input className="price-in" type="number" min={0} step="0.01" defaultValue={m.price?.output ?? ""} onBlur={(e) => e.target.value !== String(m.price?.output ?? "") && setPrice(p.id, m.id, "output", e.target.value)} />
                </td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </details>
  );
}
